import { useEffect, useRef, useState, useCallback } from "react";
import { useLatestCallback } from "@/hooks/useLatestCallback";
import { fixedSlots } from "@/lib/listIdentity";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cardImageStyle } from "@/components/Card3D";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { toast } from "sonner";
import {
  Flag, Rocket, Wind, Swords, Zap, Heart, Trophy, ArrowLeft, Crosshair,
  Hourglass, Users, Gift, Skull, Loader2, DoorOpen, Coins, Eye,
} from "lucide-react";

const SPECIAL_LABEL = { mend: "Mend", drain: "Drain", pierce: "Pierce", heavy_strike: "Heavy Strike" };
const ACTION_TINT = { dash: "#22d3ee", sprint: "#a3e635", attack: "#fbbf24", special: "#c084fc", recover: "#f43f5e" };
const TURN_SECS = 5;

// ============================================================================
// TRACK PIECES
// ============================================================================
function Token({ lane, isYou }) {
  return (
    <motion.div
      className="absolute top-1/2 z-10"
      style={{ left: `${lane.progress}%` }}
      initial={false}
      animate={{ left: `${lane.progress}%` }}
      transition={{ type: "spring", stiffness: 90, damping: 16 }}
    >
      <div className="-translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        {/* big unmistakable YOU marker */}
        {isYou && (
          <motion.div
            className="mb-1 flex flex-col items-center pointer-events-none"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            data-testid="race-you-marker"
          >
            <span className="font-display text-[13px] leading-none tracking-[0.18em] text-black px-2 py-1 rounded-md bg-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.7)]">
              YOU
            </span>
            <span className="block h-0 w-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-amber-300" />
          </motion.div>
        )}
        <div
          className={`relative rounded-full overflow-hidden bg-black shadow-lg ${isYou ? "h-14 w-14 ring-2 ring-amber-300" : "h-10 w-10"}`}
          style={{ boxShadow: `0 0 0 2px ${lane.color}, 0 0 ${isYou ? 20 : 12}px ${lane.color}${isYou ? "" : "88"}` }}
        >
          {lane.card?.front_image && (
            <img src={lane.card.front_image} alt={lane.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(lane.card)} draggable={false} />
          )}
          {lane.finished && (
            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
              <span className="font-display text-sm text-amber-300">P{lane.place}</span>
            </div>
          )}
        </div>
        {lane.last_action && !lane.finished && (
          <span className="mt-0.5 font-mono text-[8px] uppercase tracking-wide px-1 rounded bg-black/70" style={{ color: ACTION_TINT[lane.last_action] || lane.color }}>
            {lane.last_action}
          </span>
        )}
      </div>
    </motion.div>
  );
}

function Lane({ lane, gates, distance, isYou, rank }) {
  return (
    <div
      className={`flex items-stretch gap-3 rounded-lg ${isYou ? "bg-amber-300/[0.06] ring-1 ring-amber-300/25 -mx-1 px-1 py-0.5" : "opacity-95"}`}
      data-testid={`race-lane-${lane.idx}`}
    >
      {/* label */}
      <div className="w-32 sm:w-40 shrink-0 py-1.5">
        <div className="flex items-center gap-1.5">
          <span className="font-display text-xs w-5 shrink-0" style={{ color: rank === 1 ? "#fbbf24" : "rgba(255,255,255,0.4)" }}>P{rank}</span>
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: lane.color }} />
          <span className={`text-xs truncate ${isYou ? "text-amber-300 font-semibold" : "text-white/80"}`}>{lane.title}{isYou ? " · YOU" : ""}</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${lane.hp_pct}%`, backgroundColor: lane.hp_pct > 40 ? "#34d399" : lane.hp_pct > 15 ? "#fbbf24" : "#f43f5e" }} />
        </div>
        <div className="font-mono text-[9px] text-white/35 mt-0.5">spd {lane.race_speed.toLocaleString()} · {lane.hp_pct.toFixed(0)}% hp</div>
      </div>
      {/* track */}
      <div className="relative flex-1 rounded-lg bg-[hsl(220_22%_7%)] border border-white/5 overflow-visible" style={{ minHeight: 64 }}>
        <div className="absolute inset-0 mx-7">
          {gates.map((g, i) => (
            <div key={g} className="absolute top-0 bottom-0 border-l border-dashed border-white/8" style={{ left: `${(g / distance) * 100}%` }} />
          ))}
          <div className="absolute top-0 bottom-0 right-0 w-1.5"
            style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff,#fff 4px,#000 4px,#000 8px)" }} />
          <Token lane={lane} isYou={isYou} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LOBBY (status === "open")
// ============================================================================
function LobbyView({ race, raceId, navigate, refetch }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [betLane, setBetLane] = useState(null);
  const [betAmt, setBetAmt] = useState(race.min_bet || 10);
  const racers = race.racers || [];
  const humans = racers.filter((r) => !r.is_ai);
  const myBetTotal = (race.my_bets || []).reduce((s, b) => s + b.amount, 0);

  const act = async (path, after) => {
    setBusy(true);
    try { await api.post(`/race/${raceId}/${path}`); await refresh?.(); after?.(); }
    catch (e) { toast.error(apiError(e, "That didn't work")); }
    finally { setBusy(false); }
  };

  const placeBet = async () => {
    if (betLane == null) { toast.error("Pick a racer to back first"); return; }
    setBusy(true);
    try {
      await api.post(`/race/${raceId}/bet`, { lane: betLane, amount: betAmt });
      toast.success("Side bet placed — good luck!");
      await refresh?.(); refetch();
    } catch (e) { toast.error(apiError(e, "Couldn't place that bet")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <Link to="/race" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-3 sm:mb-5" data-testid="race-back">
        <ArrowLeft className="h-4 w-4" /> Gauntlet
      </Link>
      <div className="hud-pill rounded-xl sm:rounded-2xl p-3 sm:p-6 text-center relative overflow-hidden" data-testid="race-lobby">
        <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
        <DoorOpen className="h-6 w-6 sm:h-9 sm:w-9 mx-auto text-amber-300" />
        <h1 className="font-display text-xl sm:text-3xl text-white tracking-[0.05em] mt-1 sm:mt-2">WAITING ROOM</h1>
        <p className="text-white/50 text-sm mt-1">
          {racers.length}/{race.slots} racers in. Empty seats fill with power-matched AI when the timer ends.
        </p>
        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
          <span className="inline-flex items-center gap-2 font-mono text-amber-300 text-sm" data-testid="race-lobby-countdown">
            <Hourglass className="h-4 w-4" /> auto-starts in {race.seconds_open_left}s
          </span>
          {race.buy_in > 0 && (
            <span className="inline-flex items-center gap-1.5 font-mono text-emerald-300 text-sm" data-testid="race-pot">
              <Coins className="h-4 w-4" /> {race.pot.toLocaleString()}c pot · {race.buy_in.toLocaleString()}c ante
            </span>
          )}
          {race.total_pool > 0 && (
            <span className="inline-flex items-center gap-1.5 font-mono text-cyan-300 text-sm">
              <Eye className="h-4 w-4" /> {race.total_pool.toLocaleString()}c side bets
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
          {fixedSlots(race.slots).map(({ id: slotId, index: i }) => {
            const r = racers[i];
            return (
              <div key={slotId} className={`rounded-xl border p-2 ${r ? (r.is_you ? "border-amber-300 bg-amber-300/10" : "border-white/10 bg-white/[0.03]") : "border-dashed border-white/10"}`} data-testid={`lobby-seat-${i}`}>
                <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden bg-black mb-1.5 relative">
                  {r?.card?.front_image
                    ? <img src={r.card.front_image} alt="" className="w-full h-full object-cover" style={cardImageStyle(r.card)} />
                    : <div className="w-full h-full flex items-center justify-center text-white/20"><Users className="h-5 w-5" /></div>}
                  {r?.is_you && <div className="absolute top-1 right-1 bg-amber-300 text-black text-[9px] font-display px-1 rounded">YOU</div>}
                  {r && !r.is_ai && r.pool > 0 && <div className="absolute bottom-1 left-1 right-1 bg-cyan-500/80 text-black text-[9px] font-mono px-1 rounded text-center">{r.pool.toLocaleString()}c backed</div>}
                </div>
                <div className="text-[11px] truncate" style={{ color: r ? r.color : "rgba(255,255,255,0.3)" }}>
                  {r ? (r.is_ai ? r.name : r.card?.title || "Racer") : "Open seat"}
                </div>
              </div>
            );
          })}
        </div>

        {/* Spectator side-betting — only for non-racers, while the lobby is open. */}
        {!race.joined && (
          <div className="mt-6 rounded-xl border border-cyan-400/25 bg-cyan-500/[0.05] p-4 text-left" data-testid="race-bet-panel">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/80 mb-2 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Spectator side bet · pari-mutuel</div>
            <p className="text-[11px] text-white/45 mb-3">Back the human you think finishes best. Winners split the losing pool (10% house fee). {myBetTotal > 0 && <span className="text-cyan-300">You've staked {myBetTotal.toLocaleString()}c.</span>}</p>
            {humans.length === 0 ? (
              <p className="text-[12px] text-white/40">No human racers to back yet.</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {humans.map((r) => (
                    <button key={r.idx} data-testid={`race-bet-racer-${r.idx}`} onClick={() => setBetLane(r.idx)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] border transition-colors ${betLane === r.idx ? "border-cyan-300 bg-cyan-300/15 text-cyan-100" : "border-white/10 text-white/70 hover:border-white/25"}`}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                      {r.card?.title || r.name}{r.pool > 0 && <span className="text-white/35 font-mono">{r.pool.toLocaleString()}c</span>}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {[race.min_bet || 10, 50, 100, 250].map((n) => (
                    <button key={n} data-testid={`race-bet-amt-${n}`} onClick={() => setBetAmt(n)} className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-colors ${betAmt === n ? "bg-cyan-400 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{n}</button>
                  ))}
                  <Button data-testid="race-place-bet" disabled={busy || betLane == null} onClick={placeBet} className="bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-40 ml-auto">
                    <Coins className="h-4 w-4 mr-1.5" /> Bet {betAmt.toLocaleString()}c
                  </Button>
                </div>
                <p className="text-[10px] text-white/30 mt-2">Your balance: {(user?.credits ?? 0).toLocaleString()}c · betting closes when the race starts.</p>
                {race.unopposed && race.total_pool > 0 && (
                  <div data-testid="race-unopposed-warning" className="mt-3 rounded-lg border border-amber-400/40 bg-amber-400/10 p-2.5 text-[11px] leading-snug text-amber-100">
                    <span className="font-semibold text-amber-200">Heads up:</span> every bet so far is stacked on <span className="font-semibold">the same racer</span>. If nobody backs another lane before the race starts, <span className="font-semibold text-amber-200">all wagers will be refunded automatically</span> — pari-mutuel needs at least two backed racers to pay out.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex gap-3 justify-center mt-6">
          {race.joined && race.is_host && (
            <Button
              data-testid="race-start-button"
              disabled={busy || !race.can_start}
              onClick={() => act("start", refetch)}
              className="bg-amber-300 text-black hover:bg-amber-200 disabled:opacity-40"
            >
              <Rocket className="h-4 w-4 mr-1.5" /> {race.can_start ? "Start Now" : "Start enabled after 30s"}
            </Button>
          )}
          {race.joined ? (
            <Button data-testid="race-leave-button" variant="outline" disabled={busy} onClick={() => act("leave", () => navigate("/race"))} className="border-white/15 text-white/80">
              Leave{race.buy_in > 0 ? " (refunds ante)" : ""}
            </Button>
          ) : (
            <Button data-testid="race-spectate-back" variant="outline" disabled={busy} onClick={() => navigate("/race")} className="border-white/15 text-white/80">
              Back to Gauntlet
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN
// ============================================================================
export default function RaceTrackPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [chosen, setChosen] = useState(null);
  const [timeLeft, setTimeLeft] = useState(TURN_SECS);
  const submitting = useRef(false);

  const { data: race, refetch } = useQuery({
    queryKey: ["race", id],
    queryFn: async () => (await api.get(`/race/${id}`)).data,
    // SSE (below) pushes a live wake-up on every turn; this is a slow safety
    // net in case a ping is missed.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "finished" || s === "cancelled" ? false : 8000;
    },
  });

  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`${API}/race/${id}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => { qc.invalidateQueries({ queryKey: ["race", id] }); };
    return () => es.close();
  }, [id, qc]);

  const submit = useLatestCallback(async (action) => {
    if (submitting.current || !race || race.status !== "racing") return;
    if (race.you?.submitted || race.you?.finished) return;
    submitting.current = true;
    setChosen(action);                       // optimistic: show the pick immediately
    try {
      // Attacks & specials always strike the current race leader (target_idx=null
      // → backend auto-targets the lane in 1st place).
      await api.post(`/race/${id}/turn`, { action, target_idx: null });
      refetch();
    } catch (_) { setChosen(null); /* will retry next clock */ }
    finally { submitting.current = false; }
  });

  // Smooth local countdown — a fresh, stable 5s window each turn so the bar never
  // jumps with poll jitter and your turn is never skipped early. No submission by
  // the deadline defaults to Dash (server is authoritative either way).
  const canTakeTurn = race?.status === "racing" && !!race?.you && !race.you.submitted && !race.you.finished;
  useEffect(() => {
    if (!canTakeTurn) return;
    setChosen(null);
    setTimeLeft(TURN_SECS);
    const start = Date.now();
    const iv = setInterval(() => {
      const left = TURN_SECS - (Date.now() - start) / 1000;
      if (left <= 0) { clearInterval(iv); submit("dash"); }
      else setTimeLeft(left);
    }, 80);
    return () => clearInterval(iv);
  }, [race?.turn, canTakeTurn, submit]);

  if (!race) return <div className="text-center text-white/40 py-24">Heading to the track…</div>;

  if (race.status === "open") return <LobbyView race={race} raceId={id} navigate={navigate} refetch={refetch} />;
  if (race.status === "cancelled") {
    return (
      <div className="max-w-md mx-auto text-center py-24">
        <p className="text-white/60 mb-4">This lobby was cancelled.</p>
        <Button onClick={() => navigate("/race")} className="bg-amber-300 text-black">Back to Gauntlet</Button>
      </div>
    );
  }

  const you = race.you;
  const sorted = [...race.lanes].sort((a, b) => b.progress - a.progress);
  const racing = race.status === "racing";
  const myTurnOpen = racing && you && !you.submitted && !you.finished;
  const waiting = racing && you && you.submitted && !you.finished;
  const youDone = racing && you && you.finished;
  const specialLabel = SPECIAL_LABEL[you?.ability] || "Special";
  const CHOSEN_LABEL = { dash: "Dash", sprint: "Sprint", attack: "Attack", recover: "Recover", special: specialLabel };
  const chosenLabel = chosen ? CHOSEN_LABEL[chosen] : null;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
      <div className="flex items-center justify-between mb-5">
        <Link to="/race" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm" data-testid="race-back">
          <ArrowLeft className="h-4 w-4" /> Gauntlet
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/45">Turn {race.turn}</span>
          <span className="inline-flex items-center gap-1 font-mono text-[11px] text-amber-300"><Flag className="h-3.5 w-3.5" /> {race.distance}m</span>
        </div>
      </div>

      {/* TRACK */}
      <div className="hud-pill rounded-2xl p-4 sm:p-5 space-y-2.5" data-testid="race-track">
        {sorted.map((lane, i) => (
          <Lane key={lane.idx} lane={lane} gates={race.gates} distance={race.distance} isYou={!!you && lane.idx === you.idx} rank={i + 1} />
        ))}
      </div>

      {/* CONTROLS / STATUS */}
      {racing && you ? (
        <div className="hud-pill rounded-2xl p-4 sm:p-5 mt-4" data-testid="race-controls">
          <AnimatePresence mode="wait">
            {waiting || youDone ? (
              <motion.div key="waiting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2.5 py-3 text-white/65" data-testid="race-waiting">
                <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
                {youDone
                  ? <span>You're across the line — watching the rest finish…</span>
                  : <span>{chosenLabel ? <><span className="text-amber-300 font-medium">{chosenLabel}</span> locked in. </> : "Locked in. "}Waiting on <span className="text-amber-300 font-medium">{race.waiting_on}</span> racer{race.waiting_on === 1 ? "" : "s"}…</span>}
              </motion.div>
            ) : (
              <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* turn clock */}
                <div className="flex items-center gap-3 mb-3">
                  <Hourglass className={`h-4 w-4 ${timeLeft < 2 ? "text-red-400" : "text-cyan-300"}`} />
                  <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(timeLeft / TURN_SECS) * 100}%`, backgroundColor: timeLeft < 2 ? "#f43f5e" : "#22d3ee", transition: "width 80ms linear" }} />
                  </div>
                  <span className="font-mono text-xs text-white/60 w-10 text-right">{Math.max(0, timeLeft).toFixed(1)}s</span>
                </div>

                {/* attacks auto-strike the leader */}
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2 flex items-center gap-1">
                  <Crosshair className="h-3 w-3" /> Attack &amp; Special strike the race leader
                </div>

                {/* actions */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <ActionBtn id="dash" label="Dash" icon={Wind} hint="Steady run" onClick={() => submit("dash")} disabled={!myTurnOpen} selected={chosen === "dash"} color="#22d3ee" />
                  <ActionBtn id="sprint" label="Sprint" icon={Rocket} hint="+50% · 2-turn cd" onClick={() => submit("sprint")} disabled={!myTurnOpen || !you.can_sprint} selected={chosen === "sprint"} color="#a3e635" />
                  <ActionBtn id="attack" label="Attack" icon={Swords} hint="Hit the leader" onClick={() => submit("attack")} disabled={!myTurnOpen} selected={chosen === "attack"} color="#fbbf24" />
                  <ActionBtn id="special" label={specialLabel} icon={Zap} hint="Card ability" onClick={() => submit("special")} disabled={!myTurnOpen || !you.can_special} selected={chosen === "special"} color="#c084fc" />
                  <ActionBtn id="recover" label="Recover" icon={Heart} hint="Heal, lose ground" onClick={() => submit("recover")} disabled={!myTurnOpen} selected={chosen === "recover"} color="#f43f5e" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : !racing ? (
        <FinishCard race={race} you={you} navigate={navigate} />
      ) : null}

      {/* LOG */}
      <div className="hud-pill rounded-2xl p-4 mt-4 max-h-44 overflow-y-auto" data-testid="race-log">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/35 mb-2">Race feed</div>
        <div className="space-y-1">
          {[...race.log].reverse().map((e) => (
            <div key={e.t} className="text-[12px] text-white/65 flex gap-2">
              <span className="font-mono text-white/25 shrink-0">T{e.turn}</span>
              <span className={e.type === "finish" || e.type === "win" ? "text-amber-300" : e.type === "attack" ? "text-orange-300/90" : e.type === "heal" ? "text-emerald-300/90" : e.type === "sprint" ? "text-lime-300/90" : "text-white/65"}>{e.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ id, label, icon: Icon, hint, onClick, disabled, selected, color }) {
  return (
    <button data-testid={`race-action-${id}`} onClick={onClick} disabled={disabled}
      aria-pressed={selected}
      style={selected ? { borderColor: color, backgroundColor: `${color}22`, boxShadow: `0 0 14px ${color}55` } : undefined}
      className={`group relative flex flex-col items-center justify-center gap-0.5 rounded-xl border py-2.5 px-2 transition-all duration-150 disabled:opacity-30 disabled:hover:translate-y-0 ${selected ? "scale-[1.03]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:-translate-y-0.5"}`}>
      {selected && (
        <span className="absolute top-1 right-1 text-[8px] font-display tracking-wide px-1 rounded text-black" style={{ backgroundColor: color }} data-testid={`race-action-${id}-selected`}>
          PICKED
        </span>
      )}
      <Icon className="h-5 w-5" style={{ color }} />
      <span className="text-white text-xs font-medium">{label}</span>
      <span className="text-[9px] text-white/35">{hint}</span>
    </button>
  );
}

function FinishCard({ race, you, navigate }) {
  const standings = [...race.lanes].sort((a, b) => (a.place ?? 99) - (b.place ?? 99));
  const mine = you ? race.lanes.find((l) => l.idx === you.idx) : null;
  const won = mine?.place === 1;
  const results = race.results || {};
  const wonCard = results.prize && you && results.prize.lane === you.idx ? results.prize : null;
  const lostCard = results.forfeit && you && results.forfeit.lane === you.idx ? results.forfeit : null;
  const potWin = results.pot && you && results.pot.lane === you.idx ? results.pot : null;
  const replacement = results.replacement && you && results.replacement.lane === you.idx ? results.replacement : null;
  const mineReward = reward => reward && you && (reward.lane === you.idx || reward.user_id === you.user_id) ? reward : null;
  const gear = mineReward(results.gear);
  const consumable = mineReward(results.consumable);
  const token = mineReward(results.token);
  const betPayout = race.betting?.my_payout;

  return (
    <div className="hud-pill rounded-2xl p-6 mt-4 text-center relative overflow-hidden" data-testid="race-finish">
      <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
      <Trophy className={`h-10 w-10 mx-auto ${won ? "text-amber-300" : "text-white/40"}`} />
      <h2 className="font-display text-3xl text-white tracking-[0.05em] mt-2">
        {mine ? (won ? "VICTORY!" : `You finished P${mine.place}`) : "Race complete"}
      </h2>

      {wonCard && (
        <Link to={`/card/${wonCard.card_id}`} className="inline-flex items-center gap-1.5 mt-2 text-emerald-300 font-mono text-sm hover:underline" data-testid="race-prize">
          <Gift className="h-4 w-4" /> Prize unlocked: {wonCard.card_title}
        </Link>
      )}
      {replacement && <p data-testid="race-replacement-loot" className="text-amber-200 text-sm mt-2">+{replacement.credits.toLocaleString()} credits · {replacement.token.qty}× {replacement.token.name}</p>}
      {gear && <div data-testid="race-gear-loot" className="inline-flex max-w-full items-center gap-2 mt-3 text-sm"><InventoryIcon item={gear} className="h-5 w-5 shrink-0" /><InventoryName item={gear} className="break-words" /></div>}
      {consumable && <p data-testid="race-consumable-loot" className="text-emerald-200 text-sm mt-2">{consumable.qty}× {consumable.name}</p>}
      {token && <p data-testid="race-bonus-token" className="text-cyan-200 text-sm mt-2">Bonus: {token.qty || 1}× {token.name}</p>}
      {lostCard && (
        <p className="inline-flex items-center gap-1.5 mt-2 text-red-300 font-mono text-sm" data-testid="race-forfeit">
          <Skull className="h-4 w-4" /> {lostCard.card_title} {lostCard.locked_out ? "kept in your Playden, permanently locked out" : "forfeited to the store"}
        </p>
      )}
      {potWin && (
        <p className="inline-flex items-center gap-1.5 mt-2 text-emerald-300 font-mono text-sm block" data-testid="race-pot-win">
          <Coins className="h-4 w-4" /> {potWin.amount.toLocaleString()} credits from human stakes
        </p>
      )}
      {betPayout != null && betPayout > 0 && (
        <p className="inline-flex items-center gap-1.5 mt-2 text-cyan-300 font-mono text-sm block" data-testid="race-bet-win">
          <Eye className="h-4 w-4" /> Your side bet paid {betPayout.toLocaleString()}c
        </p>
      )}
      {race.betting?.i_bet && (betPayout == null || betPayout === 0) && (
        <p className="text-white/40 font-mono text-xs mt-2" data-testid="race-bet-lost">Your side bet didn't land this time.</p>
      )}
      {!wonCard && !replacement && !gear && !consumable && !token && !lostCard && !potWin && mine && !won && <p className="text-white/40 text-sm mt-1">No podium this time.</p>}

      <div className="max-w-md mx-auto mt-5 space-y-2 text-left">
        {standings.map((l) => (
          <div key={l.idx} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${you && l.idx === you.idx ? "bg-amber-300/10 border border-amber-300/30" : "bg-white/[0.03]"}`}>
            <span className="font-display text-lg w-7" style={{ color: l.place === 1 ? "#fbbf24" : "#fff" }}>P{l.place}</span>
            <div className={`h-9 w-7 rounded overflow-hidden bg-black shrink-0 rarity-${l.rarity}`}>
              {l.card?.front_image && <img src={l.card.front_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <span className="text-white text-sm flex-1 truncate">{l.title}{you && l.idx === you.idx ? " · YOU" : ""}</span>
            <span className="font-mono text-[10px] text-white/40">{l.hp_pct.toFixed(0)}% HP</span>
          </div>
        ))}
      </div>

      <div className="flex gap-3 justify-center mt-6">
        <Button data-testid="race-again" onClick={() => navigate("/race")} className="bg-amber-300 text-black hover:bg-amber-200">
          <Rocket className="h-4 w-4 mr-1.5" /> Race Again
        </Button>
      </div>
    </div>
  );
}
